import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ContentChildren,
  QueryList,
  TemplateRef,
  Directive,
  ChangeDetectionStrategy,
  AfterContentInit,
  HostBinding,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RadioBoxSize  = 'small' | 'medium' | 'large';
export type RadioBoxColor =
  | 'amarante' | 'moonstone' | 'rose' | 'mint'
  | 'semantic-red' | 'semantic-yellow' | 'semantic-green';
export type RadioBoxState = 'default' | 'disabled';
export type RadioIconPos  = 'left' | 'right' | 'none';

export interface RadioOption {
  value:         string;
  label:         string;
  iconPosition?: RadioIconPos;
  state?:        RadioBoxState;
}

// ─── Directive icône par option ───────────────────────────────────────────────

@Directive({
  selector:   '[appRadioIcon]',
  standalone: true,
})
export class RadioIconDirective {
  @Input('appRadioIcon') value!: string;
  constructor(public templateRef: TemplateRef<any>) {}
}

// ─── RadioBoxComponent ────────────────────────────────────────────────────────

@Component({
  selector:    'app-radiobox',
  standalone:  true,
  imports:     [CommonModule],
  templateUrl: './radiobox.component.html',
  styleUrls:   ['./radiobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide:     NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioBoxComponent),
      multi:       true,
    },
    {
      provide:     NG_VALIDATORS,
      useExisting: forwardRef(() => RadioBoxComponent),
      multi:       true,
    },
  ],
})
export class RadioBoxComponent
  implements ControlValueAccessor, Validator, AfterContentInit {

  // ── Apparence ────────────────────────────────────────────────────────────────
  @Input() size:         RadioBoxSize  = 'medium';
  @Input() color:        RadioBoxColor = 'amarante';
  @Input() state:        RadioBoxState = 'default';
  @Input() iconPosition: RadioIconPos  = 'none';

  // ── Contenu ──────────────────────────────────────────────────────────────────
  @Input() options: RadioOption[] = [];

  // ── Validation ───────────────────────────────────────────────────────────────
  /** Rend la sélection obligatoire */
  @Input() required     = false;
  /** Message affiché si required et aucune sélection après touch */
  @Input() errorMessage = 'Ce champ est obligatoire';

  // ── Comportement ─────────────────────────────────────────────────────────────
  @Input() groupe         = true;
  @Input() selectMultiple = false;
  @Input() fullWidth      = false;

  @HostBinding('style.width')
  get hostWidth(): string { return this.fullWidth ? '100%' : ''; }

  // ── Output ───────────────────────────────────────────────────────────────────
  @Output() selectionChange = new EventEmitter<string | string[]>();

  // ── Icônes ───────────────────────────────────────────────────────────────────
  @ContentChildren(RadioIconDirective)
  iconDirs!: QueryList<RadioIconDirective>;

  private _iconMap = new Map<string, TemplateRef<any>>();

  ngAfterContentInit(): void {
    this._buildMap();
    this.iconDirs.changes.subscribe(() => this._buildMap());
  }

  private _buildMap(): void {
    this._iconMap.clear();
    this.iconDirs.forEach(d => this._iconMap.set(d.value, d.templateRef));
  }

  getIcon(value: string): TemplateRef<any> | null {
    return this._iconMap.get(value) ?? null;
  }

  // ── State interne (signals) ───────────────────────────────────────────────────
  private _value    = signal<string | string[]>('');
  isTouched         = signal(false);

  // ── CVA ──────────────────────────────────────────────────────────────────────
  private _onChange:  (v: any) => void = () => {};
  private _onTouched: ()       => void = () => {};

  writeValue(val: string | string[]): void {
    this._value.set(val ?? (this._isMulti ? [] : ''));
  }
  registerOnChange(fn: any):  void { this._onChange = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }

  // ── Validator ─────────────────────────────────────────────────────────────────
  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;
    const v = this._value();
    const empty = this._isMulti ? (v as string[]).length === 0 : !v;
    return empty ? { required: true } : null;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  get _isMulti(): boolean {
    return !this.groupe && this.selectMultiple;
  }

  /** Affiche l'erreur uniquement après que l'utilisateur a interagi */
  get showError(): boolean {
    if (!this.required || !this.isTouched()) return false;
    const v = this._value();
    return this._isMulti ? (v as string[]).length === 0 : !v;
  }

  isChecked(value: string): boolean {
    const v = this._value();
    return this._isMulti
      ? (v as string[]).includes(value)
      : v === value;
  }

  isDisabled(opt: RadioOption): boolean {
    return this.state === 'disabled' || opt.state === 'disabled';
  }

  getIconPos(opt: RadioOption): RadioIconPos {
    return opt.iconPosition ?? this.iconPosition;
  }

  getBoxClasses(opt: RadioOption): Record<string, boolean> {
    return {
      'radio-box':                  true,
      [`radio-box--${this.size}`]:  true,
      [`radio-box--${this.color}`]: true,
      'radio-box--checked':         this.isChecked(opt.value),
      'radio-box--disabled':        this.isDisabled(opt),
      'radio-box--error':           this.showError,
      'radio-box--multi':           this._isMulti,
      'radio-box--icon-left':       this.getIconPos(opt) === 'left',
      'radio-box--icon-right':      this.getIconPos(opt) === 'right',
      'radio-box--full-width':      this.fullWidth,
    };
  }

  onToggle(opt: RadioOption): void {
    if (this.isDisabled(opt)) return;

    // Marquer comme touché au premier clic
    this.isTouched.set(true);
    this._onTouched();

    if (this._isMulti) {
      const arr = [...(this._value() as string[])];
      const idx = arr.indexOf(opt.value);
      idx >= 0 ? arr.splice(idx, 1) : arr.push(opt.value);
      this._value.set(arr);
    } else {
      this._value.set(opt.value);
    }

    this._onChange(this._value());
    this.selectionChange.emit(this._value());
  }
}